import { useEffect, useMemo, useRef, useState } from "react";

import { API_BASE_URL, GOOGLE_CLIENT_ID as STATIC_GOOGLE_CLIENT_ID } from "../../../config";

const GOOGLE_SCRIPT_ID = "google-identity-services";

const googleLogo = (
  <svg viewBox="0 0 18 18" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M17.64 9.2045c0-.638-.0573-1.252-.1636-1.8425H9v3.4885h4.844c-.2085 1.1256-.8341 2.0796-1.7775 2.7195l2.8725 2.2275c1.68-1.5465 2.6415-3.825 2.6415-6.593z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-0.8055 5.9565-2.2101l-2.8725-2.2275c-.798 0.5346-1.818 0.8496-3.084 0.8496-2.3709 0-4.3794-1.6029-5.0989-3.7545L0.933 13.0845C2.4045 16.125 5.4609 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.9015 10.6575c-.18-.534-.2826-1.104-.2826-1.6575s.1026-1.1235.2826-1.6575L0.933 4.9155C0.3285 6.1575 0 7.5435 0 9c0 1.4565.3285 2.8425.933 4.0845l2.9685-2.427z"
    />
    <path
      fill="#EA4335"
      d="M9 3.564c1.3209 0 2.5029.4545 3.429 1.3455l2.571-2.571C13.4609.84 11.43 0 9 0 5.4609 0 2.4045 1.875 0.933 4.9155l2.9685 2.427C4.7205 5.1909 6.6291 3.564 9 3.564z"
    />
  </svg>
);

export const GoogleSignInButton = ({
  loading,
  suspended,
  onCredential,
  onError,
  className,
  buttonClassName,
  iconWrapperClassName,
  labelReady = "Sign in with Google",
  labelLoading = "Signing in…",
}) => {
  const containerRef = useRef(null);
  const nativeButtonRef = useRef(null);
  const initRef = useRef({ initialized: false, clientId: null });
  const [clientId, setClientId] = useState(STATIC_GOOGLE_CLIENT_ID || "");
  const [ready, setReady] = useState(false);

  const hasCredentialHandler = typeof onCredential === "function";
  const disabled = loading || !ready || !hasCredentialHandler;

  useEffect(() => {
    if (STATIC_GOOGLE_CLIENT_ID) return;

    let ignore = false;
    const controller = new AbortController();

    (async () => {
      try {
        let publicConfigUrl = `${API_BASE_URL}/api/public-config/`;
        if (!API_BASE_URL) {
          publicConfigUrl = "/api/public-config/";
        }
        if (typeof window !== "undefined" && window.location?.origin) {
          try {
            const url = new URL(publicConfigUrl);
            url.searchParams.set("origin", window.location.origin);
            publicConfigUrl = url.toString();
          } catch {
            const separator = publicConfigUrl.includes("?") ? "&" : "?";
            publicConfigUrl = `${publicConfigUrl}${separator}origin=${encodeURIComponent(
              window.location.origin
            )}`;
          }
        }

        const response = await fetch(publicConfigUrl, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load public config (${response.status})`);
        }

        const data = await response.json();
        if (!ignore && data?.google_client_id) {
          setClientId(data.google_client_id);
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Unable to fetch Google login config:", error);
        if (!ignore && typeof onError === "function") {
          onError("Failed to load Google login.");
        }
      }
    })();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [onError]);

  useEffect(() => {
    if (!clientId || suspended || !containerRef.current) {
      setReady(false);
      return undefined;
    }

    setReady(false);
    let hideNativeButtonTimeout = null;

    const renderGoogleButton = () => {
      if (!containerRef.current || !window.google?.accounts?.id) {
        return;
      }

      const { initialized, clientId: initializedClientId } = initRef.current || {};
      if (!initialized || initializedClientId !== clientId) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            if (response.credential) {
              if (!hasCredentialHandler || typeof onCredential !== "function") {
                console.error("GoogleSignInButton requires a valid onCredential handler.");
                if (typeof onError === "function") {
                  onError("Google login is unavailable right now. Please try again.");
                }
                setReady(true);
                return;
              }
              try {
                await onCredential(response.credential);
              } catch (error) {
                console.error("Google login failed:", error);
                if (typeof onError === "function") {
                  onError("Google login failed. Please try again.");
                }
              } finally {
                setReady(true);
              }
            } else if (typeof onError === "function") {
              onError("Google login failed. Please try again.");
              setReady(true);
            }
          },
        });
        initRef.current = { initialized: true, clientId };
      }

      containerRef.current.innerHTML = "";

      window.google.accounts.id.renderButton(containerRef.current, {
        theme: "filled_black",
        size: "large",
        width: "100%",
        type: "standard",
        shape: "pill",
        text: "signin_with",
        logo_alignment: "left",
      });

      const hideNativeButton = () => {
        const nativeButton =
          containerRef.current?.querySelector(
            "div[role='button'], button, .gsi-material-button"
          ) || null;
        if (!nativeButton) {
          return false;
        }
        nativeButton.style.opacity = "0";
        nativeButton.style.pointerEvents = "none";
        nativeButton.style.position = "absolute";
        nativeButton.style.top = "0";
        nativeButton.style.right = "0";
        nativeButton.style.bottom = "0";
        nativeButton.style.left = "0";
        nativeButton.style.width = "100%";
        nativeButton.style.height = "100%";
        nativeButton.style.margin = "0";
        nativeButton.setAttribute("aria-hidden", "true");
        nativeButtonRef.current = nativeButton;
        return true;
      };

      if (!hideNativeButton()) {
        hideNativeButtonTimeout = window.setTimeout(() => {
          hideNativeButton();
          setReady(true);
        }, 50);
      } else {
        setReady(true);
      }
    };

    const script = document.getElementById(GOOGLE_SCRIPT_ID);

    if (window.google?.accounts?.id) {
      renderGoogleButton();
    } else if (script) {
      if (script.getAttribute("data-loaded")) {
        renderGoogleButton();
      } else {
        script.addEventListener("load", renderGoogleButton);
      }
    } else {
      const newScript = document.createElement("script");
      newScript.id = GOOGLE_SCRIPT_ID;
      newScript.src = "https://accounts.google.com/gsi/client";
      newScript.async = true;
      newScript.defer = true;
      newScript.onload = () => {
        newScript.setAttribute("data-loaded", "true");
        renderGoogleButton();
      };
      newScript.onerror = () => {
        if (typeof onError === "function") {
          onError("Failed to load Google login.");
        }
        setReady(false);
      };
      document.head.appendChild(newScript);
    }

    return () => {
      const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
      existingScript?.removeEventListener("load", renderGoogleButton);
      if (hideNativeButtonTimeout) {
        window.clearTimeout(hideNativeButtonTimeout);
      }
      nativeButtonRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [clientId, suspended, onCredential, onError, hasCredentialHandler]);

  const label = useMemo(() => (loading ? labelLoading : labelReady), [labelReady, labelLoading, loading]);

  const handleClick = () => {
    if (disabled) return;
    const nativeButton = nativeButtonRef.current;
    if (nativeButton) {
      nativeButton.click();
    } else if (typeof onError === "function") {
      onError("Google sign-in is still preparing. Please try again.");
    }
  };

  if (!clientId) return null;

  return (
    <div className={className}>
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-slate-400">
        <span className="h-px flex-1 bg-slate-300" />
        <span>Or continue with</span>
        <span className="h-px flex-1 bg-slate-300" />
      </div>
      <div className="flex pt-6">
        <div className="relative w-full">
          <button
            type="button"
            onClick={handleClick}
            className={buttonClassName}
            disabled={disabled}
          >
            <span className={iconWrapperClassName} aria-hidden="true">
              {googleLogo}
            </span>
            <span className="flex-1 text-center">{label}</span>
          </button>
          <div ref={containerRef} className="pointer-events-none absolute inset-0 opacity-0" aria-hidden="true" />
          {!ready && (
            <div className="gsi-loading-overlay">
              <span className="loading-dot animate-pulse" />
              <span className="loading-text">Preparing Google sign-in…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
